/**
 * Utility functions for entity determination and filtering
 */

export type EntityType = string;
type FieldGroup = { list: any[]; obj: Record<string, any> };
type WorkspaceSchema = {
  fieldsByObjectSlug?: Record<string, FieldGroup>;
  metricsByObjectSlug?: Record<string, FieldGroup>;
  metrics?: FieldGroup;
};

const EMPTY_GROUP: FieldGroup = { list: [], obj: {} };

/**
 * Determines the entity type from a field name
 */
export const determineEntityFromField = (
  field?: string,
  entityRelation?: EntityType,
  workspaceSchema?: WorkspaceSchema
): EntityType => {
  if (!field) return entityRelation || "accounts";

  // Extract prefix from dotted field name (e.g., "meetings.created_at" -> "meetings")
  const dotIdx = field.indexOf(".");
  if (dotIdx > 0) {
    const prefix = field.substring(0, dotIdx);
    // Normalize contacts -> users
    const normalized = prefix === "contacts" ? "users" : prefix;
    // Validate against schema if available
    if (workspaceSchema?.fieldsByObjectSlug?.[normalized]) {
      return normalized;
    }
    // Even without schema validation, trust the prefix
    return normalized;
  }

  // No prefix — check schema field groups to determine entity
  if (workspaceSchema?.fieldsByObjectSlug) {
    for (const slug of Object.keys(workspaceSchema.fieldsByObjectSlug)) {
      if (workspaceSchema.fieldsByObjectSlug[slug]?.obj?.[field]) {
        return slug;
      }
    }
  }
  if (workspaceSchema?.metricsByObjectSlug) {
    for (const slug of Object.keys(workspaceSchema.metricsByObjectSlug)) {
      if (workspaceSchema.metricsByObjectSlug[slug]?.obj?.[field]) {
        return slug;
      }
    }
  }

  return entityRelation || "accounts";
};

const toGroup = (list: any[]): FieldGroup => ({
  list,
  obj: list.reduce((acc: Record<string, any>, item: any) => {
    if (item?.name) acc[item.name] = item;
    return acc;
  }, {}),
});

/**
 * Gets fields for a given entity
 */
export const getTraitsForEntity = (
  entity: EntityType,
  workspaceSchema?: WorkspaceSchema
) => {
  const baseGroup = workspaceSchema?.fieldsByObjectSlug?.[entity] || EMPTY_GROUP;
  const list = (baseGroup?.list || []).filter((item: any) => item?.attribute_type !== "metric_traits");
  return toGroup(list);
};

/**
 * Gets all fields for an entity (fields + metrics merged), matching AccountsPage pattern.
 * Use this when you need the full set of traits and success metrics together.
 */
export const getAllFieldsForEntity = (
  entity: EntityType,
  workspaceSchema?: WorkspaceSchema
) => {
  const baseList = workspaceSchema?.fieldsByObjectSlug?.[entity]?.list || [];
  const scopedMetrics = workspaceSchema?.metricsByObjectSlug?.[entity]?.list || [];
  const globalMetrics = (workspaceSchema?.metrics?.list || []).filter((item: any) => (
    !item?.object_slug || item?.object_slug === entity
  ));
  const metricsList = scopedMetrics.length ? scopedMetrics : globalMetrics;
  const merged = [...baseList, ...metricsList];
  const deduped = Object.values(
    merged.reduce((acc: Record<string, any>, item: any) => {
      if (item?.name) acc[item.name] = item;
      return acc;
    }, {}),
  );
  return toGroup(deduped);
};

/**
 * Gets metrics for a given entity
 */
export const getMetricsForEntity = (
  entity: EntityType,
  workspaceSchema?: WorkspaceSchema
) => {
  const scopedMetrics = workspaceSchema?.metricsByObjectSlug?.[entity]?.list || [];
  const fallbackMetrics = (workspaceSchema?.metrics?.list || []).filter((item: any) => (
    !item?.object_slug || item?.object_slug === entity
  ));
  const metricsFromFields = (workspaceSchema?.fieldsByObjectSlug?.[entity]?.list || []).filter(
    (item: any) => item?.attribute_type === "metric_traits",
  );

  const merged = [...(scopedMetrics.length ? scopedMetrics : fallbackMetrics), ...metricsFromFields];
  if (!merged.length) return EMPTY_GROUP;

  const deduped = Object.values(
    merged.reduce((acc: Record<string, any>, item: any) => {
      if (item?.name) acc[item.name] = item;
      return acc;
    }, {}),
  );
  return toGroup(deduped);
};
